import React, { Component, ReactNode } from 'react';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import ItemStatusComponent from '../ItemStatusComponent';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import { TransactionJsonModel } from '../../domain/stream/transaction/TransactionDomains';
import TransactionService from '../../service/TransactionService';
import { ConfirmPopup } from 'primereact/confirmpopup';
import LanguageUtil from '../../util/LanguageUtil';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import FolderView from '../folder/view/FolderView';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import Icon from '../../icons/Icon';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentUtil from '../../util/ComponentUtil';
import Order = query.Order;
import moment from 'moment';

class TransactionListViewerComponent extends Component<Props> {
  private transactionService: TransactionService = ServiceContainer.getInstance().get(ServiceNames.CHANNEL);

  public render() {
    const {
      views,
      transactions,
      loading,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && views.length > 0 && transactions && transactions.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={transactions}
          selectionMode='checkbox'
          selection={selectedComponentIds}
          componentName={ComponentName.TRANSACTION}
          openNewTab={true}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          loading={loading}
          onRowClick={this.openEditor}
          onSelectionChange={this.onSelectionChange}
          columns={this.getColumns()}
          onSortChange={onSortChange}
          data-testid={`${ComponentName.TRANSACTION}_list`}
        />
        <ListDraggableItem itemType={ComponentName.TRANSACTION} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.TRANSACTION} />
    );
  }

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openTransaction } = this.props;
    const name = e.data.name;

    openTransaction(name);
  };

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.transaction.table.name', defaultMessage: 'Name' }),
        className: 'min-w-[220px]',
        copyable: true,
        onCopy: (e: TransactionJsonModel) => {
          return e.name;
        },
        body: (record: TransactionJsonModel) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          return view ? (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={35} text={record.name} data-testid={`${ComponentName.TRANSACTION}_name_text_${record.name}`} />
            </div>
          ) : null;
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: 'pages.transaction.table.status', defaultMessage: 'Stage' }),
        className: 'w-1/6',
        body: (record: TransactionJsonModel) => {
          return (
            <ItemStatusComponent
              data-testid={`${ComponentName.TRANSACTION}_stage_text_${record.name}`}
              status={record.stage}
              containerClassName={'justify-start'}
            />
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.transaction.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[320px]',
        body: (record: TransactionJsonModel) => {
          return (
            <CustomTableContentWrapper
              text={record.description || LanguageUtil.get({ id: 'common.label.noDescriptionTransaction', defaultMessage: 'This transaction does not have any description yet.' })}
              clipSize={52}
              data-testid={`${ComponentName.TRANSACTION}_description_text_${record.name}`}
            />
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.transaction.table.createdAt', defaultMessage: 'Created At' }),
        className: 'w-1/6',
        body: (record: TransactionJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'pages.transaction.table.updatedAt', defaultMessage: 'Updated At' }),
        className: 'w-1/6',
        body: (record: TransactionJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit('Transaction')) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.transaction.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: TransactionJsonModel) => {
    const { permissions } = this.props;
    const actions: ReactNode[] = [];
    const permission = permissions[record.name];
    const allowed = ['Edit', 'Admin'];

    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.TRANSACTION, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            const { onUndeploy } = this.props;
            onUndeploy(record);
          }}
          data-testid={`${ComponentName.TRANSACTION}_undeploy_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.transactionService.removeEvent(record),
              message: LanguageUtil.get({ id: 'messages.confirm.remove1', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
          data-testid={`${ComponentName.TRANSACTION}_remove_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.transactionService.deployEvent(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
        data-testid={`${ComponentName.TRANSACTION}_deploy_button_${record.name}`}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );
    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };
}

interface Props extends ReduxProps {
  transactions: TransactionJsonModel[];
  openTransaction: (id: string) => void;
  currentModal?: any;
  views: any;
  loading: boolean;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
  onUndeploy: (transaction: TransactionJsonModel) => void;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TransactionListViewerComponent);
